/**
 * Chip amounts are stored as integer cents (1/100 of the major unit) for
 * every currency. Display uses Intl.NumberFormat with the room's ISO code
 * and the viewer's locale. Missing / unknown codes fall back to USD.
 *
 * IDR is ISO 2-decimal but cash games never use sen; we force whole rupiah
 * so it matches KRW (and the lobby mocks). Other zero-decimal codes follow Intl.
 */

import { t } from '../i18n';

export const DEFAULT_CURRENCY = 'USD';

/** ISO 4217 codes we treat as whole numbers even if CLDR disagrees. */
const FORCE_ZERO_DECIMAL = new Set(['IDR']);

const EUROZONE = new Set([
  'AT', 'BE', 'CY', 'EE', 'FI', 'FR', 'DE', 'GR', 'IE', 'IT', 'LV', 'LT',
  'LU', 'MT', 'NL', 'PT', 'SK', 'SI', 'ES', 'HR', 'AD', 'MC', 'SM', 'VA',
  'ME', 'XK',
]);

const REGION_CURRENCY = {
  US: 'USD', GB: 'GBP', IN: 'INR', ID: 'IDR', CA: 'CAD', AU: 'AUD',
  CH: 'CHF', KR: 'KRW',
};

export const FEATURED_CURRENCIES = [
  { code: 'USD', symbol: '$', name: 'US dollar' },
  { code: 'GBP', symbol: '£', name: 'British pound' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'INR', symbol: '₹', name: 'Indian rupee' },
  { code: 'IDR', symbol: 'Rp', name: 'Indonesian rupiah' },
  { code: 'CAD', symbol: 'C$', name: 'Canadian dollar' },
  { code: 'AUD', symbol: 'A$', name: 'Australian dollar' },
  { code: 'CHF', symbol: 'CHF', name: 'Swiss franc' },
  { code: 'KRW', symbol: '₩', name: 'Korean won' },
];

const FEATURED_BY_CODE = Object.fromEntries(FEATURED_CURRENCIES.map((c) => [c.code, c]));

/** 10 million major units — KRW/IDR buy-ins need headroom the old $10k cap did not have. */
export const MAX_BET_CENTS = 1_000_000_000;

export function viewerLocale() {
  try {
    if (typeof navigator !== 'undefined' && navigator.language) {
      return navigator.language;
    }
  } catch {
    /* ignore */
  }
  return 'en-US';
}

export function resolveCurrency(code) {
  if (!code || typeof code !== 'string') return DEFAULT_CURRENCY;
  const up = code.trim().toUpperCase();
  if (!/^[A-Z]{3}$/.test(up)) return DEFAULT_CURRENCY;
  try {
    new Intl.NumberFormat('en', { style: 'currency', currency: up }).format(0);
    return up;
  } catch {
    return DEFAULT_CURRENCY;
  }
}

export function currencyFromLocale(tag) {
  try {
    const loc = new Intl.Locale(tag || viewerLocale());
    const region = loc.region || loc.maximize().region;
    if (!region) return DEFAULT_CURRENCY;
    if (REGION_CURRENCY[region]) return REGION_CURRENCY[region];
    if (EUROZONE.has(region)) return 'EUR';
    return DEFAULT_CURRENCY;
  } catch {
    return DEFAULT_CURRENCY;
  }
}

export function currencyFractionDigits(currency) {
  const code = resolveCurrency(currency);
  if (FORCE_ZERO_DECIMAL.has(code)) return 0;
  try {
    const n = new Intl.NumberFormat('en', {
      style: 'currency',
      currency: code,
    }).resolvedOptions().maximumFractionDigits;
    return Number.isFinite(n) ? n : 2;
  } catch {
    return 2;
  }
}

export function isZeroDecimalCurrency(currency) {
  return currencyFractionDigits(currency) === 0;
}

export function currencySymbol(code, locale) {
  const resolved = resolveCurrency(code);
  if (FEATURED_BY_CODE[resolved]) return FEATURED_BY_CODE[resolved].symbol;
  try {
    const parts = new Intl.NumberFormat(locale || viewerLocale(), {
      style: 'currency',
      currency: resolved,
      currencyDisplay: 'narrowSymbol',
    }).formatToParts(0);
    return parts.find((p) => p.type === 'currency')?.value || resolved;
  } catch {
    return resolved;
  }
}

export function currencyName(code, locale) {
  const resolved = resolveCurrency(code);
  if (FEATURED_BY_CODE[resolved]) return FEATURED_BY_CODE[resolved].name;
  try {
    return new Intl.DisplayNames([locale || viewerLocale()], { type: 'currency' }).of(resolved) || resolved;
  } catch {
    return resolved;
  }
}

export function listAllCurrencyCodes() {
  try {
    if (typeof Intl.supportedValuesOf === 'function') {
      return Intl.supportedValuesOf('currency').filter((c) => /^[A-Z]{3}$/.test(c));
    }
  } catch {
    /* fall through */
  }
  return FEATURED_CURRENCIES.map((c) => c.code);
}

/**
 * Convert dollars (number or string) to cents (integer)
 * @param {number|string} dollarAmount - Dollar amount (e.g., 10.50, "10.50")
 * @returns {number} Amount in cents (e.g., 1050)
 * @throws {Error} If input is invalid
 */
export function dollarsToCents(dollarAmount) {
  if (dollarAmount === null || dollarAmount === undefined || dollarAmount === '') {
    throw new Error('Dollar amount cannot be null, undefined, or empty');
  }

  const numericAmount = typeof dollarAmount === 'string' ? parseFloat(dollarAmount) : dollarAmount;

  if (isNaN(numericAmount) || !Number.isFinite(numericAmount)) {
    throw new Error(`Invalid dollar amount: ${dollarAmount}`);
  }

  if (numericAmount < 0) {
    throw new Error('Dollar amount cannot be negative');
  }

  return Math.round(numericAmount * 100);
}

/**
 * Convert cents (integer) to dollars (number)
 * @param {number} cents - Amount in cents (e.g., 1050)
 * @returns {number} Amount in dollars (e.g., 10.50)
 * @throws {Error} If input is invalid
 */
export function centsToDollars(cents) {
  if (cents === null || cents === undefined) {
    throw new Error('Cents amount cannot be null or undefined');
  }

  if (!Number.isInteger(cents)) {
    throw new Error('Cents must be an integer');
  }

  if (cents < 0) {
    throw new Error('Cents amount cannot be negative');
  }

  return cents / 100;
}

function formatOptions(currency) {
  const code = resolveCurrency(currency);
  const digits = currencyFractionDigits(code);
  return {
    style: 'currency',
    currency: code,
    currencyDisplay: 'narrowSymbol',
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  };
}

/**
 * Format cents as a currency string for the given ISO code and locale.
 * Defaults keep existing USD / en-US call sites and tests unchanged.
 */
export function formatCurrency(cents, currency, locale) {
  const roundedCents = Math.round(Number(cents) || 0);
  const major = roundedCents / 100;
  try {
    return new Intl.NumberFormat(locale || viewerLocale(), formatOptions(currency)).format(major);
  } catch {
    const dollars = roundedCents / 100;
    return `$${dollars.toFixed(2)}`;
  }
}

/**
 * Parse user currency input and convert to cents.
 * Both '.' and ',' work as a decimal (iPhone decimal key is locale-specific).
 * When both appear, the last one is the decimal; a single separator followed
 * by 1–2 digits is a decimal, 3+ digits is grouping. Zero-decimal currencies
 * (KRW, IDR) round to a whole major unit.
 */
export function parseCurrencyInput(input, locale, currency) {
  if (input === null || input === undefined) {
    throw new Error('Input must be a non-empty string');
  }
  const raw = String(input).trim();
  if (!raw) {
    throw new Error('Input must be a non-empty string');
  }

  const digits = currencyFractionDigits(currency);
  let s = raw.replace(/[^\d.,\s'\u00a0-]/g, '');
  s = s.replace(/[\s\u00a0']/g, '');
  if (!s || s === '-' || s === '.' || s === ',') {
    throw new Error('Invalid currency format. Use format like "10.50" or "10"');
  }

  const lastComma = s.lastIndexOf(',');
  const lastDot = s.lastIndexOf('.');
  if (lastComma >= 0 && lastDot >= 0) {
    const decPos = Math.max(lastComma, lastDot);
    const decChar = s[decPos];
    const grpChar = decChar === ',' ? '.' : ',';
    s = s.split(grpChar).join('');
    s = `${s.slice(0, s.lastIndexOf(decChar)).replace(/[.,]/g, '')}.${s.slice(s.lastIndexOf(decChar) + 1)}`;
  } else if (lastComma >= 0 || lastDot >= 0) {
    const sep = lastComma >= 0 ? ',' : '.';
    const parts = s.split(sep);
    if (parts.length > 2 || (parts[1] && parts[1].length > 2)) {
      s = parts.join('');
    } else {
      s = `${parts[0]}.${parts[1] || ''}`;
    }
  }

  if (s.startsWith('.')) s = `0${s}`;
  if (!/^\d+(\.\d*)?$/.test(s)) {
    throw new Error('Invalid currency format. Use format like "10.50" or "10"');
  }

  let cents = Math.round(dollarsToCents(s));
  if (digits === 0) {
    cents = Math.round(cents / 100) * 100;
  }
  return cents;
}

/** iOS: `decimal` shows '.' or ',' per locale; `numeric` is the integer pad. */
export function amountInputMode(currency) {
  return isZeroDecimalCurrency(currency) ? 'numeric' : 'decimal';
}

/**
 * Props for a money <input>. `pattern="[0-9]*"` + numeric is what iOS uses
 * for the no-decimal keypad — only apply that for KRW/IDR.
 */
export function amountInputProps(currency) {
  if (isZeroDecimalCurrency(currency)) {
    return { inputMode: 'numeric', pattern: '[0-9]*', autoComplete: 'off' };
  }
  return { inputMode: 'decimal', autoComplete: 'off' };
}

/** Major-unit string for an input field (no grouping, locale decimal). */
export function centsToInputString(cents, currency, locale) {
  const digits = currencyFractionDigits(currency);
  const major = Math.round(Number(cents) || 0) / 100;
  try {
    return new Intl.NumberFormat(locale || viewerLocale(), {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
      useGrouping: false,
    }).format(major);
  } catch {
    return digits === 0 ? String(Math.round(major)) : major.toFixed(digits);
  }
}

/** Whole major units, no grouping. Prefer centsToInputString for amount fields. */
export function centsToWholeMajor(cents) {
  return String(Math.round((Number(cents) || 0) / 100));
}

export function isValidBetAmount(cents, minCents = 1, maxCents = MAX_BET_CENTS) {
  return Number.isInteger(cents) && cents >= minCents && cents <= maxCents;
}

/**
 * Per-currency table defaults, all in major units. Starting chips are a clean
 * round number worth roughly US$50 (not an exact conversion); blinds keep a
 * similar stack depth. Anything not listed uses the USD-style defaults.
 */
const TABLE_DEFAULTS = {
  INR: { buyIn: 4000, sb: 50, bb: 100, presets: [[10, 20], [25, 50], [50, 100], [100, 200]], snap: 0.25 },
  KRW: { buyIn: 70000, sb: 1000, bb: 2000, presets: [[500, 1000], [1000, 2000], [5000, 10000], [10000, 20000]], snap: 100 },
  IDR: { buyIn: 800000, sb: 10000, bb: 20000, presets: [[5000, 10000], [10000, 20000], [25000, 50000], [50000, 100000]], snap: 1000 },
};
const STANDARD_DEFAULTS = { buyIn: 50, sb: 1, bb: 2, presets: [[0.25, 0.5], [0.5, 1], [1, 2], [5, 10]], snap: 0.25 };

function tableDefaults(currency) {
  const code = resolveCurrency(currency);
  if (TABLE_DEFAULTS[code]) return TABLE_DEFAULTS[code];
  // Unlisted zero-decimal codes (JPY etc.) keep the old whole-number defaults.
  if (isZeroDecimalCurrency(code)) {
    return { buyIn: 50000, sb: 1000, bb: 2000, presets: TABLE_DEFAULTS.KRW.presets, snap: 100 };
  }
  return STANDARD_DEFAULTS;
}

const toCents = (major) => Math.round(major * 100);

/**
 * Currency is set before play and locks once the first hand is dealt, so a
 * running table never flips symbols under people's stacks.
 */
export function isCurrencyLocked(room) {
  if (!room) return false;
  if (room.status && room.status !== 'LOBBY') return true;
  return (Number(room.gameState?.handNumber) || 0) >= 1;
}

export function getDefaultBlinds(currency) {
  const d = tableDefaults(currency);
  return { smallBlind: toCents(d.sb), bigBlind: toCents(d.bb) };
}

export function getDefaultBuyInCents(currency) {
  return toCents(tableDefaults(currency).buyIn);
}

export function getBlindPresets(currency) {
  return tableDefaults(currency).presets.map(([sb, bb]) => ({ sb: toCents(sb), bb: toCents(bb) }));
}

/** HTML input step in major units. */
export function getInputStepMajor(currency) {
  return isZeroDecimalCurrency(currency) ? 1000 : 0.01;
}

/** Raise slider snap in cents. */
export function getSnapCents(currency) {
  return toCents(tableDefaults(currency).snap);
}

export function getPokerStakePresets() {
  return [
    { name: 'Micro Stakes', smallBlind: dollarsToCents('0.01'), bigBlind: dollarsToCents('0.02') },
    { name: 'Low Stakes', smallBlind: dollarsToCents('0.25'), bigBlind: dollarsToCents('0.50') },
    { name: 'Medium Stakes', smallBlind: dollarsToCents('1.00'), bigBlind: dollarsToCents('2.00') },
    { name: 'High Stakes', smallBlind: dollarsToCents('5.00'), bigBlind: dollarsToCents('10.00') },
    { name: 'Tournament', smallBlind: dollarsToCents('0.25'), bigBlind: dollarsToCents('0.50') },
  ];
}

/** Drop unnecessary ".00" from legacy "$N.00" action copy. New logs are structured. */
export function formatLegacyActionText(text) {
  return String(text || '').replace(/\$(\d+)\.00\b/g, '$$$1');
}

/**
 * Render a round-action entry. Structured `{ kind, name, amount }` is formatted
 * with the current room currency; old `{ text }` strings still render as stored.
 */
export function formatRoundAction(entry, formatMoney, locale) {
  if (!entry) return '';
  const money = typeof formatMoney === 'function' ? formatMoney : (c) => formatCurrency(c);
  if (entry.kind && entry.name) {
    const amt = entry.amount != null ? money(entry.amount) : '';
    const vars = { name: entry.name, amount: amt };
    if (entry.auto && entry.kind === 'FOLD') return t('action.foldedAuto', vars, locale);
    if (entry.auto && entry.kind === 'CHECK') return t('action.checkedAuto', vars, locale);
    switch (entry.kind) {
      case 'FOLD': return t('action.folded', vars, locale);
      case 'CHECK': return t('action.checked', vars, locale);
      case 'CALL': return t('action.called', vars, locale);
      case 'RAISE': return t('action.raisedTo', vars, locale);
      case 'ALL_IN': return t('action.allInFor', vars, locale);
      case 'RETURN': return t('action.uncalledReturned', vars, locale);
      default: break;
    }
  }
  return formatLegacyActionText(entry.text);
}

export function debugCurrency() {
  console.log('🎰 === CURRENCY UTILITIES DEBUG ===');

  const testCases = [
    { input: '10.50', expectedCents: 1050 },
    { input: '0.25', expectedCents: 25 },
    { input: '100', expectedCents: 10000 },
    { input: '$5.75', expectedCents: 575 },
    { input: '0.01', expectedCents: 1 },
  ];

  console.log('📊 Testing currency conversions:');
  testCases.forEach(({ input, expectedCents }) => {
    try {
      const cents = parseCurrencyInput(input, 'en-US');
      const backToDollars = centsToDollars(cents);
      const formatted = formatCurrency(cents, 'USD', 'en-US');

      const isCorrect = cents === expectedCents;
      console.log(`${isCorrect ? '✅' : '❌'} Input: "${input}" → ${cents} cents → ${backToDollars} dollars → ${formatted}`);

      if (!isCorrect) {
        console.error(`Expected ${expectedCents} cents, got ${cents} cents`);
      }
    } catch (error) {
      console.error(`❌ Error with input "${input}": ${error.message}`);
    }
  });

  console.log('🎯 Testing poker stake presets:');
  getPokerStakePresets().forEach((preset) => {
    console.log(`${preset.name}: ${formatCurrency(preset.smallBlind)}/${formatCurrency(preset.bigBlind)}`);
  });

  console.log('🧮 All currency utilities tested!');
}
